import { FC } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography,
} from "@mui/material";
import { makeSxStyles } from "ui";

export interface WarningConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  warningText?: string;
  confirmLabel: string;
  isSubmitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmTestId: string;
  cancelTestId: string;
}

const WarningConfirmDialog: FC<WarningConfirmDialogProps> = ({
  open,
  title,
  description,
  warningText,
  confirmLabel,
  isSubmitting,
  onConfirm,
  onCancel,
  confirmTestId,
  cancelTestId,
}) => {
  const styles = useStyles();

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <Box sx={styles.content}>
        <Typography sx={styles.title}>{title}</Typography>
        <Typography sx={styles.description}>{description}</Typography>
        {warningText && (
          <Box sx={styles.warningBanner}>
            <Typography sx={styles.warningText}>{warningText}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={styles.actions}>
        <Button
          onClick={onCancel}
          sx={styles.cancelButton}
          data-testid={cancelTestId}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isSubmitting}
          sx={styles.confirmButton}
          data-testid={confirmTestId}
        >
          <Box
            component="span"
            sx={{ visibility: isSubmitting ? "hidden" : "visible" }}
          >
            {confirmLabel}
          </Box>
          {isSubmitting && (
            <CircularProgress
              size={16}
              color="inherit"
              sx={styles.confirmSpinner}
            />
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

const useStyles = makeSxStyles(({ palette: { primary, grey } }) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    pb: "24px",
  },
  title: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: grey[100],
  },
  description: {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    color: grey[200],
  },
  warningBanner: {
    background: "#FFB4AB",
    borderRadius: "8px",
    padding: "14px 16px",
    boxShadow:
      "0px 1px 3px 0px rgba(0,0,0,0.30), 0px 4px 8px 3px rgba(0,0,0,0.15)",
  },
  warningText: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: "#690005",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  },
  cancelButton: {
    color: primary.main,
    textTransform: "none",
    borderRadius: "100px",
    padding: "10px 12px",
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
  },
  confirmButton: {
    position: "relative",
    background: primary.main,
    color: primary.contrastText,
    textTransform: "none",
    borderRadius: "100px",
    padding: "10px 24px",
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    "&:hover": { background: primary.main },
    // Keep the pill's own colors while submitting instead of MUI's default
    // greyed-out disabled look.
    "&.Mui-disabled": {
      background: primary.main,
      color: primary.contrastText,
      opacity: 0.7,
    },
  },
  confirmSpinner: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: "-8px",
    marginLeft: "-8px",
  },
}));

export default WarningConfirmDialog;
